import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useNodesState, useEdgesState, Handle, Position, NodeResizer } from '@xyflow/react';
import { Form, Tooltip } from 'antd';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  PlayCircleOutlined, 
  SendOutlined, 
  MessageOutlined, 
  ShoppingCartOutlined,
  ClockCircleOutlined, 
  CheckCircleOutlined, 
  DatabaseOutlined, 
  ApiOutlined, 
  FormOutlined, 
  FileTextOutlined,
  FileAddOutlined,
  RobotOutlined,
  StopOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  TableOutlined
} from '@ant-design/icons';
import CommonHandle from '../components/CommonHandle';
import EdgeWithSwitch from '../components/EdgeWithSwitch';
import AiStaffNodeBadge from '../components/AiStaffNodeBadge';
import OdooLogoIcon from '../../icons/OdooLogoIcon';
import { NODE_TYPE_CONFIGS } from '../constants';

const nodeIconColorMap = Object.fromEntries(NODE_TYPE_CONFIGS.map(c => [c.type, c.iconColor]));

const RESIZER_HANDLE_STYLE = { width: 8, height: 8, borderRadius: 2 };
const RESIZER_LINE_STYLE = { borderWidth: 1 };
const RESIZER_MIN_HEIGHT = 50;

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// 工作流程狀態管理 Hook
export const useWorkflowState = (isNodeSelected) => {
  const { t, isReady } = useLanguage();
  const [form] = Form.useForm();
  
  // 全局拖拽處理函數引用
  const [globalDragHandler, setGlobalDragHandler] = React.useState(null);
  
  // 在組件內部定義 initialNodes，這樣可以使用 t() 函數
  const initialNodes = useMemo(() => [
    {
      id: 'start',
      type: 'input',
      position: { x: 120, y: 200 },
      data: { 
        label: isReady ? t('workflowDesigner.defaultStartNode') : 'Start', 
        type: 'start',
        icon: PlayCircleOutlined,
        iconColor: nodeIconColorMap.start,
        onDelete: null,
      },
      draggable: true,
    },
  ], [isReady, t]);
  
  // 在組件內部定義 nodeTypes，這樣可以使用 t() 函數
  const nodeTypes = useMemo(() => {
    if (!isReady) {
      return [
        { type: 'start', label: 'Start', icon: PlayCircleOutlined, iconColor: nodeIconColorMap.start },
        { type: 'sendWhatsApp', label: 'Send WhatsApp', icon: SendOutlined, iconColor: nodeIconColorMap.sendWhatsApp },
        { type: 'sendWhatsAppTemplate', label: 'Send WhatsApp Template', icon: MessageOutlined, iconColor: nodeIconColorMap.sendWhatsAppTemplate },
        { type: 'sendWhatsAppShoppingCart', label: 'WhatsApp Shopping Cart', icon: ShoppingCartOutlined, iconColor: nodeIconColorMap.sendWhatsAppShoppingCart },
        { type: 'sendInstitutionOrderingCart', label: 'Institution Ordering Cart', icon: ShoppingCartOutlined, iconColor: nodeIconColorMap.sendInstitutionOrderingCart },
        { type: 'chatOrderingCart', label: 'Chat Shopping Cart', icon: ShoppingCartOutlined, iconColor: nodeIconColorMap.chatOrderingCart },
        { type: 'chatBot', label: 'Chat Bot', icon: RobotOutlined, iconColor: nodeIconColorMap.chatBot },
        { type: 'waitReply', label: 'Wait for User Reply', icon: ClockCircleOutlined, iconColor: nodeIconColorMap.waitReply },
        { type: 'waitForQRCode', label: 'Wait for QR Code', icon: ClockCircleOutlined, iconColor: nodeIconColorMap.waitForQRCode },
        { type: 'waitForLocation', label: 'Request Geolocation', icon: EnvironmentOutlined, iconColor: nodeIconColorMap.waitForLocation },
        { type: 'waitForEmailReply', label: 'Wait for Email Reply', icon: ClockCircleOutlined, iconColor: nodeIconColorMap.waitForEmailReply },
        { type: 'switch', label: 'Switch', icon: CheckCircleOutlined, iconColor: nodeIconColorMap.switch },
        { type: 'dataSetQuery', label: 'DataSet Query/Update', icon: DatabaseOutlined, iconColor: nodeIconColorMap.dataSetQuery },
        { type: 'processDataCell', label: 'Process Data Cell Operation', icon: TableOutlined, iconColor: nodeIconColorMap.processDataCell },
        { type: 'callApi', label: 'Trigger External API', icon: ApiOutlined, iconColor: nodeIconColorMap.callApi },
        { type: 'sendEForm', label: 'Send eForm', icon: FormOutlined, iconColor: nodeIconColorMap.sendEForm },
        { type: 'generateHtmlReport', label: 'HTML Report', icon: FileTextOutlined, iconColor: nodeIconColorMap.generateHtmlReport },
        { type: 'generateDocument', label: 'Generate Document', icon: FileAddOutlined, iconColor: nodeIconColorMap.generateDocument },
        { type: 'calendar', label: 'Calendar', icon: CalendarOutlined, iconColor: nodeIconColorMap.calendar },
        { type: 'odoo', label: 'Odoo', icon: OdooLogoIcon, iconColor: nodeIconColorMap.odoo },
        { type: 'end', label: 'End', icon: StopOutlined, iconColor: nodeIconColorMap.end },
      ];
    }
    
    return [
      { type: 'start', label: t('workflowDesigner.startNode'), icon: PlayCircleOutlined, iconColor: nodeIconColorMap.start },
      { type: 'sendWhatsApp', label: t('workflowDesigner.sendWhatsAppNode'), icon: SendOutlined, iconColor: nodeIconColorMap.sendWhatsApp },
      { type: 'sendWhatsAppTemplate', label: t('workflowDesigner.sendTemplateNode'), icon: MessageOutlined, iconColor: nodeIconColorMap.sendWhatsAppTemplate },
      { type: 'sendWhatsAppShoppingCart', label: t('workflowDesigner.shoppingCartNode'), icon: ShoppingCartOutlined, iconColor: nodeIconColorMap.sendWhatsAppShoppingCart },
      { type: 'sendInstitutionOrderingCart', label: t('workflowDesigner.institutionOrderingCartNode'), icon: ShoppingCartOutlined, iconColor: nodeIconColorMap.sendInstitutionOrderingCart },
      { type: 'chatOrderingCart', label: t('workflowDesigner.chatOrderingCartNode'), icon: ShoppingCartOutlined, iconColor: nodeIconColorMap.chatOrderingCart },
      { type: 'chatBot', label: t('workflowDesigner.chatBotNode'), icon: RobotOutlined, iconColor: nodeIconColorMap.chatBot },
      { type: 'waitReply', label: t('workflowDesigner.waitReplyNode'), icon: ClockCircleOutlined, iconColor: nodeIconColorMap.waitReply },
      { type: 'waitForQRCode', label: t('workflowDesigner.waitForQRCodeNode'), icon: ClockCircleOutlined, iconColor: nodeIconColorMap.waitForQRCode },
      { type: 'waitForLocation', label: t('workflowDesigner.waitForLocationNode'), icon: EnvironmentOutlined, iconColor: nodeIconColorMap.waitForLocation },
      { type: 'waitForEmailReply', label: t('workflowDesigner.waitForEmailReplyNode'), icon: ClockCircleOutlined, iconColor: nodeIconColorMap.waitForEmailReply },
      { type: 'switch', label: t('workflowDesigner.switchNode'), icon: CheckCircleOutlined, iconColor: nodeIconColorMap.switch },
      { type: 'dataSetQuery', label: t('workflowDesigner.dataSetQueryNode'), icon: DatabaseOutlined, iconColor: nodeIconColorMap.dataSetQuery },
      { type: 'processDataCell', label: t('workflowDesigner.processDataCellNode'), icon: TableOutlined, iconColor: nodeIconColorMap.processDataCell },
      { type: 'callApi', label: t('workflowDesigner.callApiNode'), icon: ApiOutlined, iconColor: nodeIconColorMap.callApi },
      { type: 'sendEForm', label: t('workflowDesigner.formNode'), icon: FormOutlined, iconColor: nodeIconColorMap.sendEForm },
      { type: 'generateHtmlReport', label: t('workflowDesigner.generateHtmlReportNode'), icon: FileTextOutlined, iconColor: nodeIconColorMap.generateHtmlReport },
      { type: 'generateDocument', label: t('workflowDesigner.generateDocumentNode'), icon: FileAddOutlined, iconColor: nodeIconColorMap.generateDocument },
      { type: 'calendar', label: t('workflowDesigner.calendarNode'), icon: CalendarOutlined, iconColor: nodeIconColorMap.calendar },
      { type: 'odoo', label: t('workflowDesigner.odooNode'), icon: OdooLogoIcon, iconColor: nodeIconColorMap.odoo },
      { type: 'end', label: t('workflowDesigner.endNode'), icon: StopOutlined, iconColor: nodeIconColorMap.end },
    ];
  }, [isReady, t]);

  // 雙擊節點打開屬性編輯抽屜

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // 初始化節點的 handle 層級狀態
  const initializeHandleLayers = useCallback((nodeId) => {
    setNodes(nodes => nodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            handleLayers: {
              top: 'target',    // 默認 target 在上層
              right: 'source',  // 默認 source 在上層
              bottom: 'source', // 默認 source 在上層
              left: 'target'    // 默認 target 在上層
            }
          }
        };
      }
      return node;
    }));
  }, [setNodes]);
  
  // Handle 映射表 - 根據位置和類型找到對應的 handle
  // 現在每個面都有 source 和 target handle，可以直接映射
  const getHandleMapping = useCallback((position, type) => {
    const mapping = {
      'top': {
        'source': 'top-source',
        'target': 'top-target'
      },
      'right': {
        'source': 'right-source', 
        'target': 'right-target'
      },
      'bottom': {
        'source': 'bottom-source',
        'target': 'bottom-target'
      },
      'left': {
        'source': 'left-source',
        'target': 'left-target'
      }
    };
    
    return mapping[position]?.[type] || null;
  }, []);

  // 根據 handle ID 獲取位置和類型
  const getHandleInfo = useCallback((handleId) => {
    if (handleId.includes('top-')) return { position: 'top', type: handleId.split('-')[1] };
    if (handleId.includes('right-')) return { position: 'right', type: handleId.split('-')[1] };
    if (handleId.includes('bottom-')) return { position: 'bottom', type: handleId.split('-')[1] };
    if (handleId.includes('left-')) return { position: 'left', type: handleId.split('-')[1] };
    return null;
  }, []);

  // Edge 方向切換處理函數
  const handleEdgeSwitch = useCallback((edgeId) => {
    setEdges(edges => edges.map(edge => {
      if (edge.id === edgeId) {
        // 獲取原始 handle 的位置信息
        const sourceHandleInfo = getHandleInfo(edge.sourceHandle);
        const targetHandleInfo = getHandleInfo(edge.targetHandle);
        
        if (!sourceHandleInfo || !targetHandleInfo) {
          console.error('❌ Cannot determine handle info for edge switching');
          return edge;
        }
        
        // 交換 source 和 target，並在同一面找到對應的 handle
        // 如果原本是 right-source → right-target，切換後應該是 right-target → right-source
        const newSourceHandle = getHandleMapping(targetHandleInfo.position, 'source');
        const newTargetHandle = getHandleMapping(sourceHandleInfo.position, 'target');
        
         if (!newSourceHandle || !newTargetHandle) {
           console.error('❌ Cannot find corresponding handles for edge switching');
           return edge;
         }
         
         // 更新節點的 handle 層級狀態 - 交換 z-index
         setNodes(nodes => nodes.map(node => {
           if (node.id === edge.source || node.id === edge.target) {
             const position = node.id === edge.source ? sourceHandleInfo.position : targetHandleInfo.position;
             const currentLayer = node.data.handleLayers?.[position];
             const newLayer = currentLayer === 'source' ? 'target' : 'source';
             
             return {
               ...node,
               data: {
                 ...node.data,
                 handleLayers: {
                   ...node.data.handleLayers,
                   [position]: newLayer
                 }
               }
             };
           }
           return node;
         }));
         
         const newEdge = {
           ...edge,
           source: edge.target,
           target: edge.source,
           sourceHandle: newSourceHandle,
           targetHandle: newTargetHandle,
           // 更新箭頭方向
           markerEnd: {
             ...edge.markerEnd,
             type: 'arrowclosed',
             width: 12,
             height: 12,
             color: '#1890ff'
           }
         };
         
         return newEdge;
      }
      return edge;
    }));
  }, [setEdges, getHandleMapping, getHandleInfo, nodes, setNodes]);

  // React Flow 節點類型組件定義 - 使用 useMemo 避免重新創建
  const nodeTypesComponents = useMemo(() => ({
    input: ({ id, data, selected }) => {
      const isMultiSelected = isNodeSelected ? isNodeSelected(id) : false;
      const nodeColor = data.iconColor || nodeIconColorMap[data.type] || '#52c41a';
      return (
      <div 
        style={{ 
          padding: '12px 16px', 
          borderRadius: '8px', 
          backgroundColor: '#f6ffed',
          width: '100%',
          height: '100%',
          minWidth: '120px',
          minHeight: '60px',
          boxSizing: 'border-box',
          textAlign: 'center',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: selected || isMultiSelected ? `0 0 8px ${hexToRgba(nodeColor, 0.5)}` : '0 1px 4px #0001',
          border: `2px solid ${nodeColor}`,
          outline: selected || isMultiSelected ? `2px solid ${nodeColor}` : 'none',
          outlineOffset: selected || isMultiSelected ? '2px' : '0px',
          transition: 'box-shadow 0.2s',
          cursor: 'move'
        }}
        onMouseDown={(e) => {
        }}
      >
         <NodeResizer
           isVisible={selected || isMultiSelected}
           minWidth={120}
           minHeight={RESIZER_MIN_HEIGHT}
           color={nodeColor}
           handleStyle={RESIZER_HANDLE_STYLE}
           lineStyle={RESIZER_LINE_STYLE}
         />
         {data.icon && React.createElement(data.icon, { style: { fontSize: '18px', marginBottom: '4px', color: nodeColor } })}
         <div style={{ fontSize: '12px', fontWeight: 'bold', lineHeight: '1.2' }}>{data.taskName || data.label}</div>
         
         {/* 左側 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Left}
           id="left-target"
           style={{ left: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.left === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Left}
           id="left-source"
           style={{ left: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.left === 'source' ? 1001 : 1000}
         />
         
         {/* 右側 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Right}
           id="right-target"
           style={{ right: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.right === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Right}
           id="right-source"
           style={{ right: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.right === 'source' ? 1001 : 1000}
         />
         
         {/* 底部 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Bottom}
           id="bottom-target"
           style={{ bottom: -7, left: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.bottom === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Bottom}
           id="bottom-source"
           style={{ bottom: -7, left: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.bottom === 'source' ? 1001 : 1000}
         />
        {data.aiStaffAvatarUrl && (
          <AiStaffNodeBadge avatarUrl={data.aiStaffAvatarUrl} name={data.aiStaffName} jobTitle={data.aiStaffJobTitle} />
        )}
      </div>
      );
    },
     output: ({ id, data, selected }) => {
       const isMultiSelected = isNodeSelected ? isNodeSelected(id) : false;
       const nodeColor = data.iconColor || nodeIconColorMap[data.type] || '#ff4d4f';
       return (
       <div 
         style={{ 
           padding: '12px 16px', 
           borderRadius: '8px', 
           backgroundColor: '#fff2f0',
           width: '100%',
           height: '100%',
           minWidth: '120px',
           minHeight: '60px',
           boxSizing: 'border-box',
           textAlign: 'center',
           position: 'relative',
           display: 'flex',
           flexDirection: 'column',
           alignItems: 'center',
           justifyContent: 'center',
           boxShadow: selected || isMultiSelected ? `0 0 8px ${hexToRgba(nodeColor, 0.5)}` : '0 1px 4px #0001',
           border: `2px solid ${nodeColor}`,
           outline: selected || isMultiSelected ? `2px solid ${nodeColor}` : 'none',
           outlineOffset: selected || isMultiSelected ? '2px' : '0px',
           transition: 'box-shadow 0.2s',
           cursor: 'move'
         }}
         onMouseDown={(e) => {
         }}
       >
         <NodeResizer
           isVisible={selected || isMultiSelected}
           minWidth={120}
           minHeight={RESIZER_MIN_HEIGHT}
           color={nodeColor}
           handleStyle={RESIZER_HANDLE_STYLE}
           lineStyle={RESIZER_LINE_STYLE}
         />
         {data.icon && React.createElement(data.icon, { style: { fontSize: '18px', marginBottom: '4px', color: nodeColor } })}
         <div style={{ fontSize: '12px', fontWeight: 'bold', lineHeight: '1.2' }}>{data.taskName || data.label}</div>
         
         {/* 頂部 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Top}
           id="top-target"
           style={{ top: -7, left: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.top === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Top}
           id="top-source"
           style={{ top: -7, left: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.top === 'source' ? 1001 : 1000}
         />
         
         {/* 左側 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Left}
           id="left-target"
           style={{ left: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.left === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Left}
           id="left-source"
           style={{ left: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.left === 'source' ? 1001 : 1000}
         />
         
         {/* 右側 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Right}
           id="right-target"
           style={{ right: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.right === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Right}
           id="right-source"
           style={{ right: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.right === 'source' ? 1001 : 1000}
         />
        
        {data.aiStaffAvatarUrl && (
          <AiStaffNodeBadge avatarUrl={data.aiStaffAvatarUrl} name={data.aiStaffName} jobTitle={data.aiStaffJobTitle} />
        )}
        {selected && (
          <Tooltip title={t('workflowDesigner.delete')} placement="top">
            <button 
              style={{ 
                position: 'absolute', 
                top: -8, 
                right: -8, 
                zIndex: 1000,
                background: '#ff4d4f',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                padding: '0'
              }} 
              onClick={(e) => {
                e.stopPropagation();
                if (data.onDelete) {
                  data.onDelete(id);
                }
              }}
            >
              <DeleteOutlined style={{ fontSize: '12px' }} />
            </button>
          </Tooltip>
        )}
      </div>
      );
    },
    default: ({ id, data, selected }) => {
      const isMultiSelected = isNodeSelected ? isNodeSelected(id) : false;
      const nodeColor = data.iconColor || nodeIconColorMap[data.type] || '#1890ff';
      return (
      <div 
        style={{ 
          padding: '12px 16px', 
          borderRadius: '8px', 
          backgroundColor: hexToRgba(nodeColor, 0.06),
          width: '100%',
          height: '100%',
          minWidth: '160px',
          minHeight: '60px',
          boxSizing: 'border-box',
          textAlign: 'center',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: selected || isMultiSelected ? `0 0 8px ${hexToRgba(nodeColor, 0.5)}` : '0 1px 4px #0001',
          border: `2px solid ${nodeColor}`,
          outline: selected || isMultiSelected ? `2px solid ${nodeColor}` : 'none',
          outlineOffset: selected || isMultiSelected ? '2px' : '0px',
          transition: 'box-shadow 0.2s',
          cursor: 'move'
        }}
        onMouseDown={(e) => {
        }}
      >
         <NodeResizer
           isVisible={selected || isMultiSelected}
           minWidth={160}
           minHeight={RESIZER_MIN_HEIGHT}
           color={nodeColor}
           handleStyle={RESIZER_HANDLE_STYLE}
           lineStyle={RESIZER_LINE_STYLE}
         />
         {data.icon && React.createElement(data.icon, { style: { fontSize: '18px', marginBottom: '4px', color: nodeColor } })}
         <div style={{ fontSize: '12px', fontWeight: 'bold', lineHeight: '1.2' }}>{data.taskName || data.label}</div>
         
         {/* 頂部 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Top}
           id="top-target"
           style={{ top: -7, left: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.top === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Top}
           id="top-source"
           style={{ top: -7, left: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.top === 'source' ? 1001 : 1000}
         />
         
         {/* 左側 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Left}
           id="left-target"
           style={{ left: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.left === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Left}
           id="left-source"
           style={{ left: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.left === 'source' ? 1001 : 1000}
         />
         
         {/* 右側 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Right}
           id="right-target"
           style={{ right: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.right === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Right}
           id="right-source"
           style={{ right: -7, top: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.right === 'source' ? 1001 : 1000}
         />
         
         {/* 底部 handles - 重疊在同一位置，使用 z-index 控制層級 */}
         <CommonHandle
           type="target"
           position={Position.Bottom}
           id="bottom-target"
           style={{ bottom: -7, left: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.bottom === 'target' ? 1001 : 1000}
         />
         <CommonHandle
           type="source"
           position={Position.Bottom}
           id="bottom-source"
           style={{ bottom: -7, left: '50%' }}
           t={t}
           nodeId={id}
           nodeData={data}
           zIndex={data.handleLayers?.bottom === 'source' ? 1001 : 1000}
         />
        {data.aiStaffAvatarUrl && (
          <AiStaffNodeBadge avatarUrl={data.aiStaffAvatarUrl} name={data.aiStaffName} jobTitle={data.aiStaffJobTitle} />
        )}
        {selected && data.type !== 'start' && (
          <Tooltip title={t('workflowDesigner.delete')} placement="top">
            <button 
              style={{ 
                position: 'absolute', 
                top: -8, 
                right: -8, 
                zIndex: 1000,
                background: '#ff4d4f',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                padding: '0'
              }} 
              onClick={(e) => {
                e.stopPropagation();
                if (data.onDelete) {
                  data.onDelete(id);
                }
              }}
            >
              <DeleteOutlined style={{ fontSize: '12px' }} />
            </button>
          </Tooltip>
        )}
      </div>
      );
    }
  }), [isNodeSelected, t]);

  // Edge 類型定義
  const edgeTypes = useMemo(() => ({
    default: EdgeWithSwitch,
    smoothstep: EdgeWithSwitch,
  }), []);
  
  // 選中狀態
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);
  
  // 抽屜狀態
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // 工具欄狀態
  const [isToolbarCollapsed, setIsToolbarCollapsed] = useState(false);
  
  // 連接狀態
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState(null);
  
  // React Flow 實例引用
  const reactFlowInstanceRef = useRef();
  
  // 節點類型標籤函數
  const nodeTypeLabel = useCallback((type) => nodeTypes.find(n => n.type === type)?.label || type, [nodeTypes]);
  
  // 工具欄收合切換
  const toggleToolbar = () => {
    setIsToolbarCollapsed(!isToolbarCollapsed);
  };
  
  // 用 ref 保存最新的 selectedNode，讓 handleNodeDataChange 擁有穩定 reference
  // 避免 useCallback deps 含 selectedNode 導致每次 setSelectedNode 後 handleNodeDataChange
  // reference 改變，觸發所有依賴它的 useEffect 無限重跑
  const selectedNodeRef = useRef(selectedNode);
  React.useEffect(() => { selectedNodeRef.current = selectedNode; }, [selectedNode]);

  // 處理節點數據變更
  const handleNodeDataChange = useCallback((newData) => {
    const node = selectedNodeRef.current;
    if (!node) return;
    
    const updatedNode = { ...node, data: { ...node.data, ...newData } };
    
    setNodes(nds => nds.map(n => 
      n.id === node.id 
        ? updatedNode
        : n
    ));
    
    // 更新 selectedNode 以反映最新的數據
    setSelectedNode(updatedNode);
  }, [setNodes, setSelectedNode]); // selectedNode 不放 deps，保持 stable reference
  
  // 單擊節點選擇節點（不打開屬性抽屜）
  const onNodeClick = (event, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  };

  // 雙擊節點打開屬性編輯抽屜
  const onNodeDoubleClick = (event, node) => {
    setSelectedNode(node);
    setDrawerOpen(true);
    setSelectedEdge(null);
  };

  // 處理邊選擇
  const onEdgeClick = (event, edge) => {
    setSelectedEdge(edge);
  };
  
  // 邊的懸停處理
  const onEdgeMouseEnter = (event, edge) => {
    setHoveredEdge(edge);
  };

  const onEdgeMouseLeave = () => {
    setHoveredEdge(null);
  };
  
  // 監聽視圖變化，當用戶縮放或平移時清除選中的連接線
  const onMove = useCallback(() => {
    if (selectedEdge) {
      setSelectedEdge(null);
    }
  }, [selectedEdge]);
  
  // 初始化 React Flow
  const handleInit = (instance) => {
    reactFlowInstanceRef.current = instance;
    // 只在初始載入時自動 fitView
    if (instance && instance.getNodes().length === 1 && instance.getNodes()[0].id === 'start') {
      setTimeout(() => {
        instance.fitView({ padding: 0.1 });
      }, 100);
    }
  };
  
  return {
    // 狀態
    nodes,
    setNodes,
    onNodesChange,
    edges,
    setEdges,
    onEdgesChange,
    selectedNode,
    setSelectedNode,
    selectedEdge,
    setSelectedEdge,
    hoveredEdge,
    setHoveredEdge,
    drawerOpen,
    setDrawerOpen,
    isToolbarCollapsed,
    setIsToolbarCollapsed,
    isConnecting,
    setIsConnecting,
    connectionStart,
    setConnectionStart,
    reactFlowInstanceRef,
    form,
    
    // 計算值
    nodeTypes,
    nodeTypesComponents,
    nodeTypeLabel,
    
    // 處理函數
    handleNodeDataChange,
    onNodeClick,
    onNodeDoubleClick,
    onEdgeClick,
    onEdgeMouseEnter,
    onEdgeMouseLeave,
    onMove,
    handleInit,
    toggleToolbar,
    handleEdgeSwitch,
    edgeTypes,
    
    // 拖拽處理
    setGlobalDragHandler,
  };
};
